import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    Chip
} from "@mui/material";
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Delete, HelpOutline, WarningAmber, Security, AssignmentInd, ExpandMore, ExpandLess } from '@mui/icons-material';

function AuthorizedApps() {
    // CUSTOM HOOK
    const axiosPrivate = useAxiosPrivate();
    const { enqueueSnackbar } = useSnackbar();

    // STATE
    const [revokingClient, setRevokingClient] = useState(null);

    // MUTATION & QUERY
    const {
        isLoading: authorizedClientsDataLoading,
        data: authorizedClientsData,
        refetch: authorizedClientsDataRefetch
    } = useQuery(["authorizedClients"], () => {
        return axiosPrivate.get(`oauth/authorizedClients`);
    }, { refetchOnWindowFocus: false });

    const {
        isLoading: openIdClientsDataLoading,
        data: openIdClientsData,
        refetch: openIdDataRefetch
    } = useQuery(["openIDAuthorizedClients"], () => {
        return axiosPrivate.get(`oauth/openIDAuthorizedClients`);
    }, { refetchOnWindowFocus: false });

    const { mutate, data, error, isLoading: isRevoking } = useMutation((id) => {
        return axiosPrivate.post(`oauth/revokeClient`, { client_id: id });
    });

    // USEEFFECT 
    useEffect(() => {
        if (data) {
            enqueueSnackbar(`Succesfully revoked access.`, {
                variant: "success",
                autoHideDuration: 2000
            });
            setRevokingClient(null);
            authorizedClientsDataRefetch();
            openIdDataRefetch();
        }
    }, [data, enqueueSnackbar, authorizedClientsDataRefetch, openIdDataRefetch]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(
                error?.response?.data?.error?.message || error?.message || "Network Error!",
                { variant: "error" }
            );
            setRevokingClient(null);
        }
    }, [enqueueSnackbar, error]);

    const handleRevoke = (client) => {
        setRevokingClient(client);
    };

    const confirmRevoke = () => {
        if (revokingClient) {
            mutate(revokingClient.id);
        }
    };

    // RENDERING
    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: "100%", py: 4, px: { xs: 2, md: 4 } }}>
            <Grid container spacing={4}>
                {/* Column 1: Third-party apps with account access */}
                <Grid item xs={12} md={6}>
                    <SectionHeader
                        icon={<Security sx={{ color: 'primary.main' }} />}
                        title="Third-party App Access"
                        subtitle="Apps that have specific permissions to manage your account."
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                        {authorizedClientsDataLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : (
                            authorizedClientsData?.data?.data?.map((client) => (
                                <AppCard
                                    key={client.id}
                                    client={client}
                                    onRevoke={() => handleRevoke(client)}
                                    type="full"
                                />
                            ))
                        )}
                        {(!authorizedClientsData?.data?.data || authorizedClientsData?.data?.data.length === 0) && !authorizedClientsDataLoading && (
                            <EmptyState message="No third-party apps found." />
                        )}
                    </Box>
                </Grid>

                {/* Column 2: Signing with SSO */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionHeader
                            icon={<AssignmentInd sx={{ color: 'secondary.main' }} />}
                            title="Signing with SSO"
                            subtitle="Apps that use your SSO identity for quick login."
                        />
                        <Tooltip
                            title="Apps listed here can see your basic profile information (name, email) but don't have manage permissions."
                            arrow
                        >
                            <HelpOutline sx={{ color: 'text.disabled', fontSize: 20, mb: 2 }} />
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                        {openIdClientsDataLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : (
                            openIdClientsData?.data?.data?.map((client) => (
                                <AppCard
                                    key={client.id}
                                    client={client}
                                    onRevoke={() => handleRevoke(client)}
                                    type="sso"
                                />
                            ))
                        )}
                        {(!openIdClientsData?.data?.data || openIdClientsData?.data?.data.length === 0) && !openIdClientsDataLoading && (
                            <EmptyState message="No SSO-connected apps found." />
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Revoke Confirmation Dialog */}
            <RevokeDialog
                open={!!revokingClient}
                clientName={revokingClient?.name}
                onClose={() => setRevokingClient(null)}
                onConfirm={confirmRevoke}
                loading={isRevoking}
            />
        </Box>
    );
}

// SUB-COMPONENTS

function SectionHeader({ icon, title, subtitle }) {
    return (
        <Box sx={{ mb: 1, pl: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                {icon}
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                    {title}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {subtitle}
            </Typography>
        </Box>
    );
}

function AppCard({ client, onRevoke, type }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 5,
                bgcolor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.05)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                    alt={client.name}
                    src={client.logo_url}
                    sx={{
                        width: 52,
                        height: 52,
                        border: '1px solid rgba(0,0,0,0.05)',
                        bgcolor: 'grey.50',
                        '& .MuiAvatar-img': {
                            objectFit: 'contain',
                            p: 0.5,
                        },
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                        {client.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                        Authorized on {moment(client.auth_given_at).format("MMM D, YYYY")}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {type === 'full' && (
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                            sx={{ bgcolor: 'grey.50' }}
                        >
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    )}
                    <Tooltip title="Remove Access">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={onRevoke}
                            sx={{ bgcolor: 'error.lighter' }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {expanded && type === 'full' && (
                <Box sx={{ mt: 3, pl: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'primary.main', mb: 1.5, display: 'block', letterSpacing: '0.5px' }}>
                        Granted Permissions
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {client.auth_scopes?.map((scope) => (
                            <Box key={scope.id} sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ mt: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '13px', lineHeight: 1.2 }}>
                                        {scope.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                                        {scope.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Divider sx={{ my: 2, opacity: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            Expires on {moment(client.expires_at).format("MMM D, YYYY")}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

function RevokeDialog({ open, clientName, onClose, onConfirm, loading }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { borderRadius: 6, p: 1, maxWidth: '400px' }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
                <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'error.lighter',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                }}>
                    <WarningAmber color="error" />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Remove Access?
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    Are you sure you want to revoke <strong>{clientName}</strong>'s access to your account? You will need to re-authorize it to use its services again.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    onClick={onClose}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
                >
                    Keep Access
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={onConfirm}
                    disabled={loading}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Revoke Access"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function EmptyState({ message }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 5,
                borderRadius: 5,
                textAlign: 'center',
                borderStyle: 'dashed',
                bgcolor: 'transparent',
                borderColor: 'rgba(0,0,0,0.1)'
            }}
        >
            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                {message}
            </Typography>
        </Paper>
    );
}

export default AuthorizedApps;
